import { useEffect, useState } from 'react';
import { api, type AccessTokenInfo } from '../api';
import { Badge, Button, Page } from '../ui';

/**
 * Gestão de Personal Access Tokens (PAT). Usados por clientes externos — em
 * especial o app de sincronização de docs — pra autenticar via Bearer token.
 * O valor em claro do token só aparece UMA vez, na criação.
 */
export function AccountSettings() {
  const [tokens, setTokens] = useState<AccessTokenInfo[]>([]);
  const [newLabel, setNewLabel] = useState('app de sincronização');
  const [expiresInDays, setExpiresInDays] = useState<string>(''); // '' = não expira
  const [justIssued, setJustIssued] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .listAccessTokens()
      .then(({ tokens }) => setTokens(tokens))
      .catch((e) => setErr(e.status === 403 ? 'Sem permissão' : 'Falha ao listar tokens'));
  };

  useEffect(load, []);

  async function issue() {
    setErr(null);
    setBusy(true);
    try {
      const days = expiresInDays.trim() ? Number(expiresInDays) : undefined;
      const { token } = await api.createAccessToken({
        label: newLabel.trim() || 'token',
        ...(days && days > 0 ? { expiresInDays: days } : {}),
      });
      setJustIssued(token);
      setNewLabel('app de sincronização');
      setExpiresInDays('');
      load();
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha ao criar token');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revogar este token? O app que o usa vai parar de sincronizar.')) return;
    await api.revokeAccessToken(id);
    load();
  }

  return (
    <Page
      title="Tokens de acesso"
      subtitle="Credenciais pessoais pra clientes externos (ex: app de sincronização de docs)."
    >
      <div className="settings">
        <section>
          <h2>Novo token</h2>
          {err && <div className="ui-error-banner">{err}</div>}
          {justIssued && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ color: 'var(--color-ok)' }}>
                Token criado. <strong>Copie agora — não será exibido de novo.</strong>
              </p>
              <div className="revealed-key">{justIssued}</div>
            </div>
          )}
          <div className="inline-form" style={{ marginBottom: 12, gap: 8 }}>
            <input
              placeholder="label (ex: notebook de casa)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <input
              type="number"
              min={1}
              max={3650}
              placeholder="expira em (dias, opcional)"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              style={{ maxWidth: 200 }}
            />
            <Button type="button" variant="primary" size="sm" onClick={issue} disabled={busy}>
              {busy ? 'criando…' : 'Criar token'}
            </Button>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>label</th>
                <th style={{ textAlign: 'left' }}>token</th>
                <th style={{ textAlign: 'left' }}>criado</th>
                <th style={{ textAlign: 'left' }}>último uso</th>
                <th style={{ textAlign: 'left' }}>expira</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td>{t.label}</td>
                  <td><code>{t.prefix}…</code></td>
                  <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : '—'}</td>
                  <td>
                    {t.expiresAt ? (
                      new Date(t.expiresAt).toLocaleDateString()
                    ) : (
                      <Badge variant="neutral">nunca</Badge>
                    )}
                  </td>
                  <td>
                    <Button type="button" variant="link" size="sm" onClick={() => revoke(t.id)}>
                      revogar
                    </Button>
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--color-muted)' }}>
                    Nenhum token ainda. Crie um pra conectar o app de sincronização.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </Page>
  );
}
